import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { SpecsInputField } from "./SpecsEditorFields/index";

export type PromptDialogState = {
  title: string;
  label: string;
  defaultValue: string;
  onSubmit: (value: string) => void;
};

export type SpecsEditorPromptDialogApi = {
  promptDialog: PromptDialogState | null;
  openPromptDialog: (
    title: string,
    label: string,
    defaultValue: string,
    onSubmit: (value: string) => void
  ) => void;
  closePromptDialog: () => void;
};

export function useSpecsEditorPromptDialogApi(): SpecsEditorPromptDialogApi {
  const [promptDialog, setPromptDialog] =
    React.useState<PromptDialogState | null>(null);

  const openPromptDialog = React.useCallback(
    (
      title: string,
      label: string,
      defaultValue: string,
      onSubmit: (value: string) => void
    ) => {
      setPromptDialog({ title, label, defaultValue, onSubmit });
    },
    []
  );

  const closePromptDialog = React.useCallback(() => {
    setPromptDialog(null);
  }, []);

  return React.useMemo(
    () => ({
      promptDialog,
      openPromptDialog,
      closePromptDialog,
    }),
    [closePromptDialog, openPromptDialog, promptDialog]
  );
}

type SpecsEditorPromptDialogProps = {
  promptDialog: PromptDialogState | null;
  onClose: () => void;
};

export default function SpecsEditorPromptDialog({
  promptDialog,
  onClose,
}: SpecsEditorPromptDialogProps) {
  const [promptValue, setPromptValue] = React.useState("");

  React.useEffect(() => {
    if (!promptDialog) {
      setPromptValue("");
      return;
    }
    setPromptValue(promptDialog.defaultValue);
  }, [promptDialog]);

  const handleSubmit = React.useCallback(() => {
    const trimmedValue = promptValue.trim();
    if (!trimmedValue) {
      return;
    }

    promptDialog?.onSubmit(trimmedValue);
    onClose();
  }, [onClose, promptDialog, promptValue]);

  return (
    <Dialog
      open={promptDialog !== null}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>{promptDialog?.title}</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <SpecsInputField
          autoFocus
          label={promptDialog?.label ?? ""}
          value={promptValue}
          onChange={(event) => setPromptValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSubmit();
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!promptValue.trim()}
          onClick={handleSubmit}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
